"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const V = require("validator-ts");
const R = require("./rules");
function newDriverGuard(driver, errHandler) {
    const test = (obj, scheme, path) => {
        try {
            V.validate(obj, scheme, path);
        }
        catch (err) {
            if (errHandler) {
                errHandler(err);
            }
            else {
                // tslint:disable-next-line:no-console
                console.warn(`Connex-Driver[MALFORMED RESPONSE]: ${err.message}`);
            }
        }
        return obj;
    };
    const genesis = test(driver.genesis, blockScheme, 'genesis');
    return {
        genesis,
        get head() {
            return test(driver.head, headScheme, 'head');
        },
        pollHead() {
            return driver.pollHead()
                .then(h => test(h, headScheme, 'getHead()'));
        },
        getBlock(revision) {
            return driver.getBlock(revision)
                .then(b => b ? test(b, blockScheme, 'getBlock()') : b);
        },
        getTransaction(id, allowPending) {
            return driver.getTransaction(id, allowPending)
                .then(tx => tx ? test(tx, txScheme, 'getTransaction()') : tx);
        },
        getReceipt(id) {
            return driver.getReceipt(id)
                .then(r => r ? test(r, receiptScheme, 'getReceipt()') : r);
        },
        getAccount(addr, revision) {
            return driver.getAccount(addr, revision)
                .then(a => test(a, {
                balance: R.hexString,
                energy: R.hexString,
                hasCode: R.bool
            }, 'getAccount()'));
        },
        getCode(addr, revision) {
            return driver.getCode(addr, revision)
                .then(c => test(c, {
                code: R.bytes
            }, 'getCode()'));
        },
        getStorage(addr, key, revision) {
            return driver.getStorage(addr, key, revision)
                .then(s => test(s, {
                value: R.bytes32
            }, 'getStorage()'));
        },
        explain(arg, revision) {
            return driver.explain(arg, revision)
                .then(r => test(r, [vmOutputScheme], 'explain()'));
        },
        filterEventLogs(arg) {
            return driver.filterEventLogs(arg)
                .then(r => test(r, [eventWithMetaScheme], 'filterEventLogs()'));
        },
        filterTransferLogs(arg) {
            return driver.filterTransferLogs(arg)
                .then(r => test(r, [transferWithMetaScheme], 'filterTransferLogs()'));
        },
        signTx(msg, option) {
            return driver.signTx(msg, Object.assign(Object.assign({}, option), { delegationHandler: option.delegationHandler ?
                    unsigned => {
                        test(unsigned, {
                            raw: R.bytes,
                            origin: R.address
                        }, 'delegationHandler.arg');
                        return option.delegationHandler(unsigned);
                    } : undefined }))
                .then(r => test(r, {
                txid: R.bytes32,
                signer: R.address
            }, 'signTx()'));
        },
        signCert(msg, option) {
            return driver.signCert(msg, option)
                .then(r => test(r, {
                annex: {
                    domain: R.string,
                    timestamp: R.uint64,
                    signer: R.address
                },
                signature: v => R.isHexBytes(v, 65) ? '' : 'expected 65 bytes'
            }, 'signCert()'));
        },
        isAddressOwned(addr) {
            return driver.isAddressOwned(addr)
                .then(r => test(r, R.bool, 'isAddressOwned()'));
        }
    };
}
exports.newDriverGuard = newDriverGuard;
const headScheme = {
    id: R.bytes32,
    number: R.uint32,
    timestamp: R.uint64,
    parentID: R.bytes32,
    txsFeatures: V.optional(R.uint32)
};
const blockScheme = {
    id: R.bytes32,
    number: R.uint32,
    size: R.uint32,
    parentID: R.bytes32,
    timestamp: R.uint64,
    gasLimit: R.uint64,
    beneficiary: R.address,
    gasUsed: R.uint64,
    totalScore: R.uint64,
    txsRoot: R.bytes32,
    txsFeatures: V.optional(R.uint32),
    stateRoot: R.bytes32,
    receiptsRoot: R.bytes32,
    signer: R.address,
    isTrunk: R.bool,
    transactions: [R.bytes32]
};
const txScheme = {
    id: R.bytes32,
    chainTag: R.uint8,
    blockRef: R.bytes8,
    expiration: R.uint32,
    gasPriceCoef: R.uint8,
    gas: R.uint64,
    origin: R.address,
    delegator: V.nullable(V.optional(R.address)),
    nonce: R.hexString,
    dependsOn: V.nullable(R.bytes32),
    size: R.uint32,
    clauses: [{
            to: V.nullable(R.address),
            value: R.hexString,
            data: R.bytes
        }],
    meta: V.nullable({
        blockID: R.bytes32,
        blockNumber: R.uint32,
        blockTimestamp: R.uint64
    })
};
const logMetaScheme = {
    blockID: R.bytes32,
    blockNumber: R.uint32,
    blockTimestamp: R.uint64,
    txID: R.bytes32,
    txOrigin: R.address,
    clauseIndex: R.uint32
};
const eventScheme = {
    address: R.address,
    topics: [R.bytes32],
    data: R.bytes,
    meta: () => '',
    decoded: () => ''
};
const eventWithMetaScheme = Object.assign(Object.assign({}, eventScheme), { meta: logMetaScheme });
const transferScheme = {
    sender: R.address,
    recipient: R.address,
    amount: R.hexString,
    meta: () => '',
};
const transferWithMetaScheme = Object.assign(Object.assign({}, transferScheme), { meta: logMetaScheme });
const receiptScheme = {
    gasUsed: R.uint64,
    gasPayer: R.address,
    paid: R.hexString,
    reward: R.hexString,
    reverted: R.bool,
    outputs: [{
            contractAddress: V.nullable(R.address),
            events: [eventScheme],
            transfers: [transferScheme]
        }],
    meta: {
        blockID: R.bytes32,
        blockNumber: R.uint32,
        blockTimestamp: R.uint64,
        txID: R.bytes32,
        txOrigin: R.address
    }
};
const vmOutputScheme = {
    data: R.bytes,
    vmError: R.string,
    gasUsed: R.uint64,
    reverted: R.bool,
    events: [{
            address: R.address,
            topics: [R.bytes32],
            data: R.bytes,
            meta: () => '',
            decoded: () => ''
        }],
    transfers: [{
            sender: R.address,
            recipient: R.address,
            amount: R.hexString,
            meta: () => '',
        }],
    decoded: () => ''
};
//# sourceMappingURL=data:application/json;base64,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